import { safeLocalStorage } from "./safeLocalStorage";

// Валидатор для проверки зависимых полей
export const validateDependentFields =
  (fields: string[], errorMessage: string) => (value: any, allValues: any) => {
    const isAllEmpty = fields.every(field => !allValues[field]);
    return isAllEmpty ? errorMessage : undefined;
  };

export const validateRelatedFields =
  (fields: string[], errorMessage: string) => (value: any, allValues: any) => {
    const hasErrorsInFields = fields.some(field => {
      const fieldValue = allValues[field];
      return !fieldValue || (typeof fieldValue === "string" && fieldValue.trim() === "");
    });

    return hasErrorsInFields ? errorMessage : undefined;
  };

export const composeValidators =
  (...validators: any[]) =>
  (value: any, allValues: any) =>
    validators.reduce((error, validator) => error || validator(value, allValues), undefined);

export const required =
  (isRadio = false) =>
  (value: any) => {
    if (isRadio) {
      if (value === false || value === true) {
        return undefined;
      } else {
        return "Обязательно для заполнения";
      }
    }

    if (Array.isArray(value) && value.length === 0) {
      return "Обязательно для заполнения";
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();
      return trimmedValue ? undefined : "Обязательно для заполнения";
    }

    return value ? undefined : "Обязательно для заполнения";
  };

export const testRegExp = (regExp: any, messageError: any) => (value: any) => {
  // Remove non-digit characters for numeric validation
  // This is important for fields like BIC, account numbers, etc. that may have formatting
  let valueToTest = value;
  if (typeof value === "string" && /^\d/.test(regExp)) {
    // If regex starts with digit pattern, remove non-digit chars
    valueToTest = value.replace(/\D/g, "");
  }
  return new RegExp(regExp).test(valueToTest) || !value ? undefined : messageError;
};

export const notEmptyValue = () => (value: any) => {
  return value && value.trim() === "" ? "Обязательно для заполнения" : undefined;
};

export const validateArrayNotEmpty =
  (messageError = "Нужно выбрать хотя бы один элемент") =>
  (value: unknown) => {
    if (!Array.isArray(value)) {
      return messageError;
    }

    return value.length > 0 ? undefined : messageError;
  };

// Если в локал сторадже сохранен флан disabledValidate=true, то валидация у полей на фронте отключается
export const disabledValidate = (fn: any) => {
  if (safeLocalStorage.getItem("disabledValidate") === "true") {
    return false;
  }
  return fn;
};

export const validateUrl =
  (messageError = "Некорректный URL") =>
  (value: any) => {
    if (!value) {
      return undefined; // Пустое значение считается валидным (если требуется обязательность, комбинируйте с required)
    }

    const trimmedValue = value.trim();
    const validUrlPattern = /^[A-Za-z0-9-._~:/?#[\]@!$&'()*+,;=]*$/;

    // Проверяем допустимые символы
    if (!validUrlPattern.test(trimmedValue)) {
      return messageError;
    }

    // Проверяем протокол: только https или отсутствие протокола
    if (/^[a-zA-Z]+:\/\//.test(trimmedValue) && !trimmedValue.startsWith("https://")) {
      return messageError;
    }

    // Проверяем, что URL валиден
    try {
      const urlString = trimmedValue.startsWith("https://")
        ? trimmedValue
        : `https://${trimmedValue}`;
      new URL(urlString);
      return undefined;
    } catch {
      return messageError;
    }
  };

export const validateSingleUrlInput =
  (messageError = "Введите только один URL") =>
  (value: string) => {
    if (!value) return undefined;

    const urls = value
      .split(/\s|,|;/)
      .map(url => url.trim())
      .filter(Boolean);

    if (urls.length > 1) {
      return messageError;
    }

    return undefined;
  };

// Валидатор минимальной длины
export const validateMinLength =
  (minLength: number, messageError = `Не менее ${minLength} символов`) =>
  (value: string) => {
    if (!value) return undefined;
    return value.length < minLength ? messageError : undefined;
  };

// Валидатор против только цифр
export const validateNotOnlyDigits =
  (messageError = "Не могут быть только цифры") =>
  (value: string) => {
    if (!value) return undefined;
    return /^\d+$/.test(value) ? messageError : undefined;
  };

export const validateMinAge =
  (minAge: number = 14, messageError = `Возраст должен быть не менее ${minAge} лет`) =>
  (value: string) => {
    if (!value) return undefined;

    try {
      let birthDate: Date;

      // Проверяем, если значение в формате ISO (1998-11-11T00:00:00.000Z)
      if (value.includes("T") && value.endsWith("Z")) {
        birthDate = new Date(value);

        // Проверяем, что дата валидна
        if (isNaN(birthDate.getTime())) {
          return "Некорректная дата";
        }
      }
      // Старая реализация для формата dd.mm.yyyy
      else {
        // Регулярное выражение для формата dd.mm.yyyy
        const dateRegex = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
        const match = value.match(dateRegex);

        if (!match) {
          return "Неверный формат даты. Пример: 01.02.1990";
        }

        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const year = parseInt(match[3], 10);

        // Проверяем диапазоны
        if (
          day < 1 ||
          day > 31 ||
          month < 0 ||
          month > 11 ||
          year < 1900 ||
          year > new Date().getFullYear()
        ) {
          return "Некорректная дата";
        }

        birthDate = new Date(year, month, day);

        // Проверяем, что дата соответствует введенным значениям
        if (
          birthDate.getDate() !== day ||
          birthDate.getMonth() !== month ||
          birthDate.getFullYear() !== year
        ) {
          return "Некорректная дата (например, 30 февраля)";
        }
      }

      const currentDate = new Date();
      const minDate = new Date();
      minDate.setFullYear(currentDate.getFullYear() - minAge);

      // Если дата рождения позже минимальной допустимой даты
      if (birthDate > minDate) {
        return messageError;
      }

      // Дополнительная проверка: дата не должна быть в будущем
      if (birthDate > currentDate) {
        return "Дата рождения не может быть в будущем";
      }

      return undefined;
    } catch {
      return "Некорректная дата";
    }
  };
